import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { businessPath, path } from '~/app/navigation';
import { useTranslation } from 'react-i18next';
import { OPEN_ORDER_STATUSES, computeOrderTotals, marginBps } from '@prody/shared';
import {
  Code, Delta, EmptyState, Gauge, MoneyText, PageHeader, Metric, RollingNumber,
  Section, Stamp, Workbench, ListPane,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import {
  useBalances, useOrders, usePurchaseTasks, useQuotes, useShipments,
} from '~/platform/data/queries';
import { ORDER_TONE, STOP_TONE } from '~/platform/domain/presentation';
import './dashboard.css';

/* ==================================================================
   Today.

   Not a dashboard of charts. This is the trading day as a single
   sheet: what is still to buy, what is on a van, what is short, and
   who owes money — the four questions someone opening Prody at 06:00
   actually has.

   The metrics are ledger entries, not marketing tiles: mono figures
   at table scale with stencilled labels and a rail in the tone of
   whatever needs attention. A dashboard that looks like a different
   product from the tables underneath it teaches people twice.
   ================================================================== */

export default function DashboardPage() {
  const { t } = useTranslation(['dashboard', 'common', 'orders', 'market', 'purchasing', 'logistics', 'payments']);
  const navigate = useNavigate();
  const { business, format } = useBusiness();
  const tradingDay = format.tradingDay();
  const base = businessPath(business.slug);

  const { data: orders = [], isLoading } = useOrders(business.id, {
    deliveryDate: tradingDay,
    statuses: [...OPEN_ORDER_STATUSES],
  });
  const { data: tasks = [] } = usePurchaseTasks(business.id, tradingDay);
  const { data: shipments = [] } = useShipments(business.id, tradingDay);
  const { data: quotes = [] } = useQuotes(business.id, tradingDay);
  const { data: balances = [] } = useBalances(business.id, 'receivable');

  const taxBpsFor = useMemo(() => {
    const rates = new Map(business.settings.taxScheme.rates.map((r) => [r.key, r.bps]));
    return (key: string) => rates.get(key) ?? 0;
  }, [business.settings.taxScheme]);

  const stats = useMemo(() => {
    const totals = orders.map((o) => computeOrderTotals(o, taxBpsFor));
    const bookValue = totals.reduce((sum, x) => sum + x.gross.minor, 0);
    const shortLines = totals.reduce((sum, x) => sum + x.shortLineCount, 0);
    const filled = totals.filter((x) => x.fillRate !== null);
    const fillRate = filled.length
      ? filled.reduce((sum, x) => sum + (x.fillRate ?? 0), 0) / filled.length
      : null;
    const toBuy = tasks.filter((task) => task.status === 'todo').length;
    const enRoute = shipments.filter((s) => s.status !== 'delivered' && s.status !== 'failed').length;
    const overdue = balances.reduce((sum, b) => sum + b.overdue.minor, 0);
    const moved = quotes.filter((q) => q.previousSell && q.previousSell.minor !== q.sell.minor);
    return { bookValue, shortLines, fillRate, toBuy, enRoute, overdue, moved };
  }, [orders, tasks, shipments, balances, quotes, taxBpsFor]);

  if (!isLoading && orders.length === 0 && tasks.length === 0) {
    return (
      <Workbench>
        <PageHeader eyebrow={t('eyebrow')} title={t('title')} />
        <ListPane>
          <EmptyState
            title={t('empty')}
            body={t('emptyBody', { cutoff: business.settings.orderCutoff })}
          />
        </ListPane>
      </Workbench>
    );
  }

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={format.date(tradingDay, 'weekday')}
      />

      <ListPane>
        <div className="pd-dash">
          <div className="pd-dash__metrics">
            {/* The counts are counter drums: they roll up from zero on
                load and turn over when the day moves. */}
            <Metric
              label={t('openOrders')}
              value={<RollingNumber value={orders.length} />}
              footnote={
                <MoneyText
                  value={{ minor: stats.bookValue, currency: format.currency }}
                  compact
                  size="sm"
                />
              }
              onClick={() => void navigate({ to: path(`${base}/orders`) })}
            />
            <Metric
              label={t('toBuy')}
              value={<RollingNumber value={stats.toBuy} />}
              tone={stats.toBuy > 0 ? 'accent' : undefined}
              footnote={t('purchasing:shoppingList')}
              onClick={() => void navigate({ to: path(`${base}/purchasing`) })}
            />
            <Metric
              label={t('outForDelivery')}
              value={<RollingNumber value={stats.enRoute} />}
              footnote={t('logistics:routes')}
              onClick={() => void navigate({ to: path(`${base}/logistics`) })}
            />
            <Metric
              label={t('shortLines')}
              value={<RollingNumber value={stats.shortLines} />}
              tone={stats.shortLines > 0 ? 'warn' : undefined}
              footnote={
                stats.fillRate !== null ? (
                  <Gauge value={stats.fillRate} tone={stats.fillRate > 0.95 ? 'ok' : 'warn'} label={t('fillRate')} />
                ) : (
                  t('fillRateFoot')
                )
              }
            />
            <Metric
              label={t('overdue')}
              value={
                <MoneyText
                  value={{ minor: stats.overdue, currency: format.currency }}
                  bare
                  compact
                />
              }
              tone={stats.overdue > 0 ? 'alert' : undefined}
              footnote={t('payments:receivable')}
              onClick={() => void navigate({ to: path(`${base}/payments`) })}
            />
          </div>

          <div className="pd-dash__columns">
            {/* ---- Today's orders ---- */}
            <Section title={t('orders:title')}>
              <ul className="pd-dash__list">
                {orders.slice(0, 8).map((order) => {
                  const totals = computeOrderTotals(order, taxBpsFor);
                  return (
                    <li key={order.id}>
                      <button
                        type="button"
                        className="pd-dash__row"
                        onClick={() => void navigate({ to: path(`${base}/orders`) })}
                      >
                        <Code>{order.number}</Code>
                        <span className="truncate">{order.customerName}</span>
                        <span className="num faint">{format.timeWindow(order.deliveryWindow)}</span>
                        <MoneyText value={totals.gross} bare size="sm" />
                        <Stamp tone={ORDER_TONE[order.status]} size="sm">
                          {t(`orders:status.${order.status}`)}
                        </Stamp>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Section>

            {/* ---- Prices that moved this morning ----
                The most-read block on this page in the first hour of
                the day, so it earns its place above logistics. */}
            <Section title={t('priceMoves')}>
              {stats.moved.length === 0 ? (
                <p className="dim t-small">{t('market:emptyBody')}</p>
              ) : (
                <ul className="pd-dash__list">
                  {stats.moved.slice(0, 6).map((quote) => {
                    const change =
                      quote.previousSell && quote.previousSell.minor !== 0
                        ? Math.round(
                            ((quote.sell.minor - quote.previousSell.minor) / quote.previousSell.minor) *
                              10_000,
                          )
                        : null;
                    return (
                      <li key={quote.id}>
                        <button
                          type="button"
                          className="pd-dash__row"
                          onClick={() => void navigate({ to: path(`${base}/market`) })}
                        >
                          <Code>{quote.productId.replace('prd_', '')}</Code>
                          <span className="chalk-struck num">
                            {format.money(quote.previousSell, { bare: true })}
                          </span>
                          <MoneyText value={quote.sell} bare />
                          <Delta bps={change} />
                          <span className="num faint">
                            {format.percentFromBps(marginBps(quote.sell, quote.cost) ?? 0)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>

            {/* ---- Vans out ---- */}
            <Section title={t('logistics:routes')}>
              <ul className="pd-dash__list">
                {shipments.map((shipment) => (
                  <li key={shipment.id}>
                    <button
                      type="button"
                      className="pd-dash__row"
                      onClick={() => void navigate({ to: path(`${base}/logistics`) })}
                    >
                      <span className="num faint">{shipment.sequence}</span>
                      <span className="truncate">{shipment.customerName}</span>
                      <span className="num faint">{format.timeWindow(shipment.window)}</span>
                      <Stamp tone={STOP_TONE[shipment.status]} size="sm">
                        {t(`logistics:stopStatus.${shipment.status}`)}
                      </Stamp>
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          </div>
        </div>
      </ListPane>
    </Workbench>
  );
}
